// SchedulerView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Utilities
import { timestampFromDate } from 'utilities/DateTime';
// Services
import { deleteEdge, upsertEdge } from 'services/nodes/NodeEdgeService';
import { updateNode } from 'services/nodes/NodeWriteService';

// Hooks
import useSchedulerViewData from './hooks/useSchedulerViewData';
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';

// CSS
import './SchedulerView.css';

// Components
import SchedulerViewColumnHeader from './SchedulerViewColumnHeader';
import SchedulerViewSlot from './SchedulerViewSlot';
import IconButton from 'components/iconbutton/IconButton';
import { addDays, addMinutes, differenceInMinutes, format, isSameDay, max as maxDate, min as minDate, startOfDay } from 'date-fns';
import { toast } from 'react-toastify';

const MINUTES_PER_DAY = 24 * 60;
const TOUCH_DRAG_THRESHOLD_PX = 10;

const hashStringToIndex = (value = '') => {
    const source = String(value || '').trim();
    if (!source) return 0;

    let hash = 0;

    for (let index = 0; index < source.length; index += 1) {
        hash = ((hash << 5) - hash) + source.charCodeAt(index);
        hash |= 0;
    }

    return Math.abs(hash);
};

const getHexColorLuminance = (value = '') => {
    const normalized = String(value || '').trim();
    const hex = normalized.startsWith('#') ? normalized.slice(1) : normalized;
    const expanded = hex.length === 3
        ? hex.split('').map((char) => `${char}${char}`).join('')
        : hex;

    if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;

    const red = Number.parseInt(expanded.slice(0, 2), 16) / 255;
    const green = Number.parseInt(expanded.slice(2, 4), 16) / 255;
    const blue = Number.parseInt(expanded.slice(4, 6), 16) / 255;

    return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
};

const getScheduledCardPaletteColor = (nodeKey, colorOptions, fallbackColor) => {
    const palette = (Array.isArray(colorOptions) ? colorOptions : [])
        .map((color) => String(color || '').trim())
        .filter(Boolean);

    if (!palette.length) return fallbackColor;

    const paletteIndex = hashStringToIndex(nodeKey) % palette.length;
    return palette[paletteIndex] || fallbackColor;
};

const formatFieldDisplayValue = (value, fieldType) => {
    const normalizedFieldType = String(fieldType || '').toLowerCase();

    if (value == null) return '';

    if (normalizedFieldType === 'date' || normalizedFieldType === 'datetime') {
        const date = value?.toDate && typeof value.toDate === 'function' ? value.toDate() : new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return normalizedFieldType === 'datetime'
            ? format(date, 'MMM d, h:mm a')
            : format(date, 'MMM d, yyyy');
    }

    if (normalizedFieldType === 'people') {
        const list = Array.isArray(value) ? value : [value];
        return list
            .map((entry) => String(entry?.name || entry?.label || entry?.title || entry || '').trim())
            .filter(Boolean)
            .join(', ');
    }

    if (Array.isArray(value)) {
        return value.map((entry) => String(entry || '').trim()).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
        if (value?.label) return String(value.label).trim();
        if (value?.value) return String(value.value).trim();
        if (value?.name) return String(value.name).trim();
    }

    return String(value).trim();
};

const buildTemporalValueForField = ({ fieldType, date, defaultDurationMinutes = 15 }) => {
    const safeDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(safeDate.getTime())) return null;

    if (fieldType === 'datetime') {
        return {
            startDate: timestampFromDate(safeDate),
            startPrecision: 'time',
            endDate: null,
            endPrecision: null,
            isRange: false,
        };
    }

    if (fieldType === 'datetimerange') {
        const endDate = addMinutes(safeDate, defaultDurationMinutes);
        return {
            startDate: timestampFromDate(safeDate),
            startPrecision: 'time',
            endDate: timestampFromDate(endDate),
            endPrecision: 'time',
            isRange: true,
        };
    }

    return null;
};

const applyOverlapLayout = (entries = []) => {
    if (!Array.isArray(entries) || entries.length === 0) return [];

    const sortedEntries = [...entries].sort((left, right) => {
        const startDelta = left.layout.startMinutes - right.layout.startMinutes;
        if (startDelta !== 0) return startDelta;
        const durationDelta = right.layout.durationMinutes - left.layout.durationMinutes;
        if (durationDelta !== 0) return durationDelta;
        return String(left.node?.title || '').localeCompare(String(right.node?.title || ''), undefined, { sensitivity: 'base' });
    });

    const groups = [];
    let currentGroup = [];
    let currentGroupEnd = -1;

    sortedEntries.forEach((entry) => {
        const start = entry.layout.startMinutes;
        const end = entry.layout.startMinutes + entry.layout.durationMinutes;

        if (!currentGroup.length || start < currentGroupEnd) {
            currentGroup.push(entry);
            currentGroupEnd = Math.max(currentGroupEnd, end);
            return;
        }

        groups.push(currentGroup);
        currentGroup = [entry];
        currentGroupEnd = end;
    });

    if (currentGroup.length) groups.push(currentGroup);

    return groups.flatMap((group) => {
        const laneEndByIndex = [];
        const laidOut = group.map((entry) => {
            const start = entry.layout.startMinutes;
            const end = entry.layout.startMinutes + entry.layout.durationMinutes;
            let laneIndex = laneEndByIndex.findIndex((laneEnd) => laneEnd <= start);

            if (laneIndex < 0) {
                laneIndex = laneEndByIndex.length;
                laneEndByIndex.push(end);
            } else {
                laneEndByIndex[laneIndex] = end;
            }

            return {
                ...entry,
                layout: {
                    ...entry.layout,
                    laneIndex,
                },
            };
        });

        const laneCount = Math.max(1, laneEndByIndex.length);

        return laidOut.map((entry) => ({
            ...entry,
            layout: {
                ...entry.layout,
                laneCount,
            },
        }));
    });
};

const buildFieldStorageKey = (typeDef, fieldDef, getType) => {
    const sourceTypeKey = String(fieldDef?.source || typeDef?.key || '').trim();
    if (!sourceTypeKey) return typeDef?.dataSection || null;

    const sourceDef = typeof getType === 'function' ? getType(sourceTypeKey) : null;
    if (sourceDef?.dataSection) return sourceDef.dataSection;

    return `data_${sourceTypeKey.toLowerCase()}`;
};

const buildDraggedRange = ({ startMinutes, selectedDay, durationMinutes, fieldType }) => {
    const nextStart = addMinutes(startOfDay(selectedDay), startMinutes);
    const nextEnd = addMinutes(nextStart, durationMinutes);

    return {
        range: {
            start: nextStart,
            end: nextEnd,
        },
        value: buildTemporalValueForField({
            fieldType,
            date: nextStart,
            defaultDurationMinutes: durationMinutes,
        }),
    };
};

const normalizeResourceKeys = (resourceKeys = []) => (
    [...new Set(
        (Array.isArray(resourceKeys) ? resourceKeys : [])
            .map((resourceKey) => String(resourceKey || '').trim())
            .filter(Boolean)
    )].sort()
);

const areResourceKeyListsEqual = (left = [], right = []) => {
    const normalizedLeft = normalizeResourceKeys(left);
    const normalizedRight = normalizeResourceKeys(right);

    if (normalizedLeft.length !== normalizedRight.length) return false;

    return normalizedLeft.every((resourceKey, index) => resourceKey === normalizedRight[index]);
};

const areRangesEqual = (leftRange, rightRange) => {
    const leftStart = leftRange?.start instanceof Date ? leftRange.start.getTime() : NaN;
    const leftEnd = leftRange?.end instanceof Date ? leftRange.end.getTime() : NaN;
    const rightStart = rightRange?.start instanceof Date ? rightRange.start.getTime() : NaN;
    const rightEnd = rightRange?.end instanceof Date ? rightRange.end.getTime() : NaN;

    return leftStart === rightStart && leftEnd === rightEnd;
};

const resolveSlotTargetFromPoint = (clientX, clientY) => {
    if (typeof document === 'undefined') return null;

    const targetElement = document.elementFromPoint(clientX, clientY);
    const slotElement = targetElement?.closest?.('.scheduler-view-slot-row[data-resource-key][data-start-minutes]');
    if (!slotElement) return null;

    const resourceKey = String(slotElement.getAttribute('data-resource-key') || '').trim();
    const rowKey = String(slotElement.getAttribute('data-row-key') || '').trim();
    const startMinutes = Number.parseInt(slotElement.getAttribute('data-start-minutes') || '', 10);
    if (!resourceKey || !rowKey || Number.isNaN(startMinutes)) return null;

    return {
        resourceKey,
        rowKey,
        startMinutes,
    };
};

/**
 * SchedulerView
 *
 * Renders the Scheduler day grid for the selected resource columns.
 *
 * @param {object} view - Active view definition.
 * @param {object} typeDef - Resource type definition.
 * @param {object[]} nodes - Visible resource nodes.
 * @param {string} [displayMode='full'] - Whether the view is in full or compact mode.
 *
 * @returns {ReactNode} The rendered scheduler view.
 */
const SchedulerView = ({
    view,
    typeDef,
    nodes,
    displayMode = 'full',
    onQuickActionCreateOnly,
    onQuickActionSelect,
}) => {

    // ----- CONTEXT ----- //

    const { getType } = useNodeTypes();
    const { theme } = useTheme();
    const { user } = useUser();

    // ----- HANDLERS ----- //

    /**
     * Opens a node using the shared default navigation behavior.
     */

    const handleOpenNode = useDefaultNodeOpen();

    // ----- STATE ----- //

    const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
    const [draggingCard, setDraggingCard] = useState(null);
    const [dragOverSlot, setDragOverSlot] = useState(null);
    const [armedMoveCard, setArmedMoveCard] = useState(null);
    const [detachingCardKey, setDetachingCardKey] = useState('');
    const [optimisticEntryOverrides, setOptimisticEntryOverrides] = useState({});
    const [resizingCard, setResizingCard] = useState(null);
    const [touchDragState, setTouchDragState] = useState(null);
    // ----- REFS ----- //

    const suppressNextCardOpenRef = useRef(false);
    const resizeSessionRef = useRef(null);

    const schedulerConfig = view?.schedulerConfig && typeof view.schedulerConfig === 'object'
        ? view.schedulerConfig
        : {};

    const slotIncrementMinutes = Number.parseInt(schedulerConfig?.slotIncrementMinutes, 10) || 15;
    const scheduledFieldKey = String(schedulerConfig?.scheduledField || '').trim();

    // ----- MEMOS ----- //

    const scheduledProperties = useMemo(() => (
        Array.isArray(schedulerConfig?.scheduledProperties) ? schedulerConfig.scheduledProperties : []
    ), [schedulerConfig?.scheduledProperties]);
    const safeNodes = useMemo(() => (
        Array.isArray(nodes) ? nodes.filter((node) => node?.key) : []
    ), [nodes]);
    const rowHeight = displayMode === 'compact' ? 36 : 48;

    // ----- HOOKS ----- //

    const {
        scheduledTypeDef,
        scheduledFieldDef,
        scheduledSchemaFieldMap,
        resolveScheduledFieldValue,
        isLoading,
        error,
        scheduledEntries,
    } = useSchedulerViewData({
        view,
        resourceNodes: safeNodes,
        selectedDay,
    });

    // ----- MEMOS ----- //

    const scheduledPropertyDefs = useMemo(() => (
        scheduledProperties
            .map((fieldKey) => scheduledSchemaFieldMap.get(fieldKey) || null)
            .filter(Boolean)
    ), [scheduledProperties, scheduledSchemaFieldMap]);

    const scheduledFieldStorageKey = useMemo(() => (
        buildFieldStorageKey(scheduledTypeDef, scheduledFieldDef, getType)
    ), [getType, scheduledFieldDef, scheduledTypeDef]);

    const effectiveScheduledEntries = useMemo(() => (
        scheduledEntries.map((entry) => {
            const override = optimisticEntryOverrides?.[entry.key] || null;
            if (!override) return entry;
            return {
                ...entry,
                ...override,
                range: override.range || entry.range,
                resourceKeys: Array.isArray(override.resourceKeys) ? override.resourceKeys : entry.resourceKeys,
            };
        })
    ), [optimisticEntryOverrides, scheduledEntries]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setOptimisticEntryOverrides((prev) => {
            const overrideEntries = Object.entries(prev || {});
            if (!overrideEntries.length) return prev;

            const scheduledEntryMap = new Map(
                scheduledEntries.map((entry) => [entry.key, entry])
            );

            let didChange = false;
            const nextOverrides = {};

            overrideEntries.forEach(([entryKey, override]) => {
                const scheduledEntry = scheduledEntryMap.get(entryKey);
                if (!scheduledEntry) {
                    didChange = true;
                    return;
                }

                const overrideRange = override?.range || scheduledEntry.range;
                const overrideResourceKeys = Array.isArray(override?.resourceKeys)
                    ? override.resourceKeys
                    : scheduledEntry.resourceKeys;

                const hasCaughtUp = areRangesEqual(scheduledEntry.range, overrideRange)
                    && areResourceKeyListsEqual(scheduledEntry.resourceKeys, overrideResourceKeys);

                if (hasCaughtUp) {
                    didChange = true;
                    return;
                }

                nextOverrides[entryKey] = override;
            });

            return didChange ? nextOverrides : prev;
        });
    }, [scheduledEntries]);

    // ----- MEMOS ----- //

    const gridHeight = useMemo(() => {
        return Math.ceil(MINUTES_PER_DAY / slotIncrementMinutes) * rowHeight;
    }, [rowHeight, slotIncrementMinutes]);

    const timeRows = useMemo(() => {
        const rowCount = Math.ceil(MINUTES_PER_DAY / slotIncrementMinutes);
        return Array.from({ length: rowCount }, (_, index) => {
            const minutes = index * slotIncrementMinutes;
            const hour = Math.floor(minutes / 60);
            const minute = minutes % 60;
            const label = minute === 0
                ? format(new Date(2026, 0, 1, hour, minute), 'h a')
                : format(new Date(2026, 0, 1, hour, minute), 'h:mm a');

            return {
                key: `row_${minutes}`,
                label,
                minutes,
                top: index * rowHeight,
                isHour: minute === 0,
            };
        });
    }, [rowHeight, slotIncrementMinutes]);

    const columnEntriesByResourceKey = useMemo(() => {
        const dayStart = startOfDay(selectedDay);
        const dayEnd = addDays(dayStart, 1);
        const map = new Map(safeNodes.map((node) => [String(node.key), []]));

        effectiveScheduledEntries.forEach((entry) => {
            const visibleStart = maxDate([entry.range.start, dayStart]);
            const visibleEnd = minDate([entry.range.end, dayEnd]);
            const startMinutes = Math.max(0, differenceInMinutes(visibleStart, dayStart));
            const endMinutesRaw = Math.max(startMinutes, differenceInMinutes(visibleEnd, dayStart));
            const endMinutes = Math.min(MINUTES_PER_DAY, endMinutesRaw);
            const durationMinutes = Math.max(slotIncrementMinutes, endMinutes - startMinutes || slotIncrementMinutes);

            const layout = {
                top: (startMinutes / slotIncrementMinutes) * rowHeight,
                height: Math.max((durationMinutes / slotIncrementMinutes) * rowHeight, rowHeight),
                startMinutes,
                durationMinutes,
            };

            entry.resourceKeys.forEach((resourceKey) => {
                const list = map.get(String(resourceKey)) || [];
                list.push({
                    ...entry,
                    layout,
                });
                map.set(String(resourceKey), list);
            });
        });

        map.forEach((list, resourceKey) => {
            const laidOutEntries = applyOverlapLayout(list);
            map.set(resourceKey, laidOutEntries);
        });

        return map;
    }, [effectiveScheduledEntries, rowHeight, safeNodes, selectedDay, slotIncrementMinutes]);

    const isConfigured = Boolean(scheduledTypeDef?.key && scheduledFieldKey);
    const canQuickAdd = isConfigured && typeof (onQuickActionSelect || onQuickActionCreateOnly) === 'function';
    const canDragCards = Boolean(scheduledFieldStorageKey && scheduledFieldDef?.type);
    const canResizeCards = Boolean(scheduledFieldStorageKey && String(scheduledFieldDef?.type || '').toLowerCase() === 'datetimerange');
    const scheduledCardColorOptions = Array.isArray(theme.colorOptions) ? theme.colorOptions : [];

    // ----- HANDLERS ----- //

    /**
     * handleSlotCreate
     *
     * Opens the scheduled-type quick create flow from a scheduler slot.
     *
     * @param {object} resourceNode - The resource node for the clicked column.
     * @param {number} startMinutes - Slot start offset in minutes from the selected day.
     * @returns {Promise<void>} Resolves when the quick-create handoff completes.
     */

    const handleSlotCreate = useCallback(async (resourceNode, startMinutes) => {
        if (!scheduledTypeDef?.key || !scheduledFieldDef?.type || !scheduledFieldKey) return;

        const handler = onQuickActionSelect || onQuickActionCreateOnly;
        if (typeof handler !== 'function') return;

        const slotDate = addMinutes(startOfDay(selectedDay), startMinutes);
        const temporalValue = buildTemporalValueForField({
            fieldType: scheduledFieldDef.type,
            date: slotDate,
            defaultDurationMinutes: 15,
        });
        if (!temporalValue) return;

        const dateFieldOption = {
            fieldKey: scheduledFieldKey,
            fieldLabel: scheduledFieldDef?.label || '',
            fieldType: String(scheduledFieldDef.type || '').toLowerCase(),
            value: temporalValue,
        };

        const createdNode = await handler(scheduledTypeDef.key, {
            quickAdd: true,
            relatedNode: resourceNode,
            shouldNavigate: false,
            initialFields: {
                [scheduledFieldKey]: temporalValue,
            },
            quickAddConfig: {
                dateFieldOptions: [dateFieldOption],
                defaultDateFieldKey: scheduledFieldKey,
            },
        });

        if (createdNode?.key && resourceNode?.key) {
            try {
                await upsertEdge(resourceNode.key, createdNode.key, {
                    baseKey: view?.baseKey || null,
                });
            } catch (error) {
                console.error('[SchedulerView] link new scheduled node error:', error);
            }
        }
    }, [
        onQuickActionCreateOnly,
        onQuickActionSelect,
        scheduledFieldDef,
        scheduledFieldKey,
        scheduledTypeDef?.key,
        selectedDay,
        view?.baseKey,
    ]);

    // ----- MEMOS ----- //

    const resetDragState = useCallback(() => {
        setDraggingCard(null);
        setDragOverSlot(null);
    }, []);

    const resetMoveState = useCallback(() => {
        setArmedMoveCard(null);
    }, []);

    const resetTouchDragState = useCallback(() => {
        setTouchDragState(null);
        setDragOverSlot(null);
    }, []);

    const resetResizeState = useCallback(() => {
        resizeSessionRef.current = null;
        setResizingCard(null);
        setTimeout(() => {
            suppressNextCardOpenRef.current = false;
        }, 0);
    }, []);

    // ----- HANDLERS ----- //

    /**
     * handleCardClick
     *
     * Opens the scheduled node from the scheduler card.
     *
     * @param {object} event - Click event from the scheduler card.
     * @param {object} entry - Scheduled entry represented by the card.
     */

    const handleCardClick = useCallback((event, entry) => {
        if (suppressNextCardOpenRef.current) {
            suppressNextCardOpenRef.current = false;
            event?.preventDefault?.();
            event?.stopPropagation?.();
            return;
        }

        event?.preventDefault?.();
        handleOpenNode(entry?.node);
    }, [handleOpenNode]);

    /**
     * handleCardKeyDown
     *
     * Opens the scheduled node when the focused card receives Enter or Space.
     *
     * @param {object} event - Keyboard event from the scheduler card.
     * @param {object} entry - Scheduled entry represented by the card.
     */
    const handleCardKeyDown = useCallback((event, entry) => {
        if (event?.key !== 'Enter' && event?.key !== ' ') return;

        event.preventDefault();
        handleOpenNode(entry?.node);
    }, [handleOpenNode]);

    /**
     * handleCardTouchStart
     *
     * Starts a touch drag session for the selected scheduler card.
     *
     * @param {object} event - Touch start event from the scheduler card.
     * @param {object} entry - Scheduled entry represented by the card.
     * @param {object} resourceNode - Resource node represented by the current column.
     */
    const handleCardTouchStart = useCallback((event, entry, resourceNode) => {
        if (!canDragCards) return;
        const touch = event?.touches?.[0] || null;
        const entryKey = String(entry?.key || '').trim();
        const sourceResourceKey = String(resourceNode?.key || '').trim();
        if (!touch || !entryKey || !sourceResourceKey) return;

        setTouchDragState({
            currentClientX: touch.clientX,
            currentClientY: touch.clientY,
            entryKey,
            hasMoved: false,
            sourceResourceKey,
            startClientX: touch.clientX,
            startClientY: touch.clientY,
            target: null,
            title: String(entry?.node?.title || 'Untitled').trim() || 'Untitled',
        });
    }, [canDragCards, getType]);

    /**
     * handleMoveEntry
     *
     * Persists a scheduler card move for time changes, resource reassignment, or both.
     *
     * @param {object} entry - Scheduled entry being moved.
     * @param {string} sourceResourceKey - Resource key the move starts from.
     * @param {string} targetResourceKey - Resource key the move ends on.
     * @param {number} startMinutes - Target slot offset in minutes from the selected day.
     * @returns {Promise<void>} Resolves when the move flow completes.
     */
    const handleMoveEntry = useCallback(async (entry, sourceResourceKey, targetResourceKey, startMinutes) => {
        if (!entry?.node?.key || !scheduledFieldDef?.type || !scheduledFieldStorageKey) return;
        const normalizedSourceResourceKey = String(sourceResourceKey || '').trim();
        const normalizedTargetResourceKey = String(targetResourceKey || '').trim();
        const fieldName = scheduledFieldDef?.name || scheduledFieldDef?.key || scheduledFieldKey;
        const rawDurationMinutes = differenceInMinutes(entry.range.end, entry.range.start);
        const durationMinutes = Math.max(slotIncrementMinutes, rawDurationMinutes || slotIncrementMinutes);
        const originalStartMinutes = differenceInMinutes(entry.range.start, startOfDay(selectedDay));
        const shouldUpdateTime = originalStartMinutes !== startMinutes;
        const shouldUpdateResource = normalizedSourceResourceKey
            && normalizedTargetResourceKey
            && normalizedSourceResourceKey !== normalizedTargetResourceKey;
        const currentSectionData = scheduledFieldStorageKey
            && entry.node?.[scheduledFieldStorageKey]
            && typeof entry.node[scheduledFieldStorageKey] === 'object'
            ? entry.node[scheduledFieldStorageKey]
            : {};
        const previousFieldValue = currentSectionData?.[fieldName] ?? null;

        if (!shouldUpdateTime && !shouldUpdateResource) {
            resetDragState();
            resetMoveState();
            return;
        }

        const nextRangePayload = buildDraggedRange({
            startMinutes,
            selectedDay,
            durationMinutes,
            fieldType: scheduledFieldDef.type,
        });

        if (!nextRangePayload.value) {
            resetDragState();
            resetMoveState();
            return;
        }

        const previousOverride = optimisticEntryOverrides?.[entry.key] || null;
        const nextResourceKeys = shouldUpdateResource
            ? [...new Set(
                entry.resourceKeys
                    .filter((resourceKey) => String(resourceKey || '').trim() !== normalizedSourceResourceKey)
                    .concat(normalizedTargetResourceKey)
            )]
            : entry.resourceKeys;

        setOptimisticEntryOverrides((prev) => ({
            ...(prev || {}),
            [entry.key]: {
                resourceKeys: nextResourceKeys,
                range: shouldUpdateTime ? nextRangePayload.range : entry.range,
            },
        }));

        resetDragState();
        resetMoveState();

        let didUpdateTime = false;
        let didAddTargetEdge = false;

        try {
            if (shouldUpdateTime) {
                await updateNode(entry.node.key, {
                    [scheduledFieldStorageKey]: {
                        ...currentSectionData,
                        [fieldName]: nextRangePayload.value,
                    },
                }, { actor: user });
                didUpdateTime = true;
            }

            if (shouldUpdateResource) {
                await upsertEdge(normalizedTargetResourceKey, entry.node.key, {
                    baseKey: view?.baseKey || null,
                });
                didAddTargetEdge = true;
                await deleteEdge(normalizedSourceResourceKey, entry.node.key, {
                    baseKey: view?.baseKey || null,
                });
            }
        } catch (error) {
            const rollbackTasks = [];

            if (didAddTargetEdge) {
                rollbackTasks.push(
                    deleteEdge(normalizedTargetResourceKey, entry.node.key, {
                        baseKey: view?.baseKey || null,
                    })
                );
            }

            if (shouldUpdateResource) {
                rollbackTasks.push(
                    upsertEdge(normalizedSourceResourceKey, entry.node.key, {
                        baseKey: view?.baseKey || null,
                    })
                );
            }

            if (didUpdateTime) {
                rollbackTasks.push(
                    updateNode(entry.node.key, {
                        [scheduledFieldStorageKey]: {
                            ...currentSectionData,
                            [fieldName]: previousFieldValue,
                        },
                    }, { actor: user })
                );
            }

            if (rollbackTasks.length) {
                const rollbackResults = await Promise.allSettled(rollbackTasks);
                rollbackResults.forEach((result) => {
                    if (result.status === 'rejected') {
                        console.error('[SchedulerView] move rollback error:', result.reason);
                    }
                });
            }

            setOptimisticEntryOverrides((prev) => {
                const next = { ...(prev || {}) };
                if (previousOverride) {
                    next[entry.key] = previousOverride;
                } else {
                    delete next[entry.key];
                }
                return next;
            });
            toast.error(`Could not move scheduled card: ${error?.message || 'Unknown error'}`);
        }
    }, [
        getType,
        optimisticEntryOverrides,
        resetDragState,
        resetMoveState,
        scheduledFieldDef,
        scheduledFieldKey,
        scheduledFieldStorageKey,
        selectedDay,
        slotIncrementMinutes,
        user,
        view?.baseKey,
    ]);

    const handleCardDragStart = useCallback((entry, resourceNode, event) => {
        if (!entry?.key || !resourceNode?.key || !canDragCards) return;
        suppressNextCardOpenRef.current = true;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', entry.key);
        setDraggingCard({
            entryKey: entry.key,
            sourceResourceKey: resourceNode.key,
        });
    }, [canDragCards, getType]);

    const handleCardDragEnd = useCallback(() => {
        resetDragState();
        setTimeout(() => {
            suppressNextCardOpenRef.current = false;
        }, 0);
    }, [resetDragState]);

    /**
     * handleResizeStart
     *
     * Starts a bottom-edge resize interaction for a scheduled card.
     *
     * @param {object} event - Mouse event from the resize handle.
     * @param {object} entry - Scheduled entry represented by the card.
     */
    const handleResizeStart = useCallback((event, entry) => {
        if (!canResizeCards || !entry?.key || !entry?.node?.key || !scheduledFieldDef?.type || !scheduledFieldStorageKey) return;
        event?.preventDefault?.();
        event?.stopPropagation?.();

        suppressNextCardOpenRef.current = true;
        resetDragState();
        resetMoveState();

        const fieldName = scheduledFieldDef?.name || scheduledFieldDef?.key || scheduledFieldKey;
        const currentSectionData = scheduledFieldStorageKey
            && entry.node?.[scheduledFieldStorageKey]
            && typeof entry.node[scheduledFieldStorageKey] === 'object'
            ? entry.node[scheduledFieldStorageKey]
            : {};
        const startDurationMinutes = Math.max(
            slotIncrementMinutes,
            differenceInMinutes(entry.range.end, entry.range.start) || slotIncrementMinutes,
        );
        const startOffsetMinutes = Math.max(0, differenceInMinutes(entry.range.start, startOfDay(selectedDay)));
        const maxDurationMinutes = Math.max(slotIncrementMinutes, MINUTES_PER_DAY - startOffsetMinutes);

        resizeSessionRef.current = {
            currentDurationMinutes: startDurationMinutes,
            currentSectionData,
            entryKey: entry.key,
            fieldName,
            maxDurationMinutes,
            previousOverride: optimisticEntryOverrides?.[entry.key] || null,
            rangeStart: entry.range.start,
            resourceKeys: Array.isArray(entry.resourceKeys) ? entry.resourceKeys : [],
            startClientY: event.clientY,
            startDurationMinutes,
        };

        setResizingCard({
            currentDurationMinutes: startDurationMinutes,
            entryKey: entry.key,
        });
    }, [
        canResizeCards,
        getType,
        optimisticEntryOverrides,
        resetDragState,
        resetMoveState,
        scheduledFieldDef,
        scheduledFieldKey,
        scheduledFieldStorageKey,
        selectedDay,
        slotIncrementMinutes,
    ]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!resizingCard?.entryKey) return undefined;

        /**
         * handleWindowMouseMove
         *
         * Handles window mouse move.
         *
         * @param {object} event - event.
         */
        const handleWindowMouseMove = (event) => {
            const session = resizeSessionRef.current;
            if (!session) return;

            const deltaY = event.clientY - session.startClientY;
            const snappedSlotDelta = Math.round(deltaY / rowHeight);
            const nextDurationMinutes = Math.max(
                slotIncrementMinutes,
                Math.min(
                    session.maxDurationMinutes,
                    session.startDurationMinutes + (snappedSlotDelta * slotIncrementMinutes),
                ),
            );

            if (nextDurationMinutes === session.currentDurationMinutes) return;

            session.currentDurationMinutes = nextDurationMinutes;
            const nextRange = {
                start: session.rangeStart,
                end: addMinutes(session.rangeStart, nextDurationMinutes),
            };

            setResizingCard((current) => {
                if (!current?.entryKey || current.entryKey !== session.entryKey) return current;
                return {
                    ...current,
                    currentDurationMinutes: nextDurationMinutes,
                };
            });

            setOptimisticEntryOverrides((prev) => ({
                ...(prev || {}),
                [session.entryKey]: {
                    resourceKeys: session.resourceKeys,
                    range: nextRange,
                },
            }));
        };

        /**
         * handleWindowMouseUp
         *
         * Handles window mouse up.
         * @returns {Promise<void>} Resolves when the handler completes.
         */
        const handleWindowMouseUp = async () => {
            const session = resizeSessionRef.current;
            if (!session) {
                resetResizeState();
                return;
            }

            // End the interactive resize immediately so follow-up mouse movement
            // cannot keep stretching the optimistic card while the save is pending.
            resetResizeState();

            const didResize = session.currentDurationMinutes !== session.startDurationMinutes;

            if (!didResize) {
                setOptimisticEntryOverrides((prev) => {
                    const next = { ...(prev || {}) };
                    if (session.previousOverride) {
                        next[session.entryKey] = session.previousOverride;
                    } else {
                        delete next[session.entryKey];
                    }
                    return next;
                });
                return;
            }

            const nextValue = buildTemporalValueForField({
                fieldType: scheduledFieldDef?.type,
                date: session.rangeStart,
                defaultDurationMinutes: session.currentDurationMinutes,
            });

            if (!nextValue) {
                return;
            }

            try {
                await updateNode(session.entryKey, {
                    [scheduledFieldStorageKey]: {
                        ...session.currentSectionData,
                        [session.fieldName]: nextValue,
                    },
                }, { actor: user });
            } catch (error) {
                setOptimisticEntryOverrides((prev) => {
                    const next = { ...(prev || {}) };
                    if (session.previousOverride) {
                        next[session.entryKey] = session.previousOverride;
                    } else {
                        delete next[session.entryKey];
                    }
                    return next;
                });
                toast.error(`Could not resize scheduled card: ${error?.message || 'Unknown error'}`);
            }
        };

        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', handleWindowMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleWindowMouseMove);
            window.removeEventListener('mouseup', handleWindowMouseUp);
        };
    }, [resetResizeState, rowHeight, resizingCard?.entryKey, scheduledFieldDef?.type, scheduledFieldStorageKey, slotIncrementMinutes, user]);

    useEffect(() => {
        if (!touchDragState?.entryKey) return undefined;

        /**
         * handleWindowTouchMove
         *
         * Handles window touch move.
         *
         * @param {object} event - event.
         */
        const handleWindowTouchMove = (event) => {
            const touch = event?.touches?.[0] || null;
            if (!touch) return;

            const deltaX = touch.clientX - touchDragState.startClientX;
            const deltaY = touch.clientY - touchDragState.startClientY;
            const hasCrossedThreshold = touchDragState.hasMoved
                || Math.hypot(deltaX, deltaY) >= TOUCH_DRAG_THRESHOLD_PX;
            const nextTarget = hasCrossedThreshold
                ? resolveSlotTargetFromPoint(touch.clientX, touch.clientY)
                : null;

            if (hasCrossedThreshold) {
                event.preventDefault();
            }

            setTouchDragState((current) => {
                if (!current?.entryKey) return current;

                return {
                    ...current,
                    currentClientX: touch.clientX,
                    currentClientY: touch.clientY,
                    hasMoved: hasCrossedThreshold,
                    target: nextTarget,
                };
            });

            setDragOverSlot(nextTarget);
        };

        const finishTouchDrag = async () => {
            const dropTarget = touchDragState?.hasMoved ? touchDragState.target : null;
            const movedEntry = effectiveScheduledEntries.find((entry) => entry.key === touchDragState?.entryKey) || null;

            if (dropTarget && movedEntry?.node?.key) {
                suppressNextCardOpenRef.current = true;
                await handleMoveEntry(
                    movedEntry,
                    touchDragState.sourceResourceKey,
                    dropTarget.resourceKey,
                    dropTarget.startMinutes,
                );
                resetTouchDragState();
                setTimeout(() => {
                    suppressNextCardOpenRef.current = false;
                }, 0);
                return;
            }

            resetTouchDragState();
        };

        /**
         * handleWindowTouchEnd
         *
         * Handles window touch end.
         */
        const handleWindowTouchEnd = () => {
            void finishTouchDrag();
        };

        /**
         * handleWindowTouchCancel
         *
         * Handles window touch cancel.
         */
        const handleWindowTouchCancel = () => {
            resetTouchDragState();
        };

        window.addEventListener('touchmove', handleWindowTouchMove, { passive: false });
        window.addEventListener('touchend', handleWindowTouchEnd);
        window.addEventListener('touchcancel', handleWindowTouchCancel);

        return () => {
            window.removeEventListener('touchmove', handleWindowTouchMove);
            window.removeEventListener('touchend', handleWindowTouchEnd);
            window.removeEventListener('touchcancel', handleWindowTouchCancel);
        };
    }, [effectiveScheduledEntries, handleMoveEntry, resetTouchDragState, touchDragState]);

    // ----- HANDLERS ----- //

    const handleSlotDragOver = useCallback((resourceNode, row, event) => {
        if (!draggingCard?.entryKey || !resourceNode?.key || !row) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        const nextTarget = {
            resourceKey: resourceNode.key,
            rowKey: row.key,
            startMinutes: row.minutes,
        };

        const isSameTarget = dragOverSlot
            && dragOverSlot.resourceKey === nextTarget.resourceKey
            && dragOverSlot.rowKey === nextTarget.rowKey;

        if (!isSameTarget) setDragOverSlot(nextTarget);
    }, [dragOverSlot, draggingCard?.entryKey]);

    const handleSlotDrop = useCallback(async (resourceNode, row, event) => {
        if (!draggingCard?.entryKey || !resourceNode?.key || !row || !scheduledFieldDef?.type || !scheduledFieldStorageKey) return;

        event.preventDefault();

        const movedEntry = effectiveScheduledEntries.find((entry) => entry.key === draggingCard.entryKey) || null;
        if (!movedEntry?.node?.key) {
            resetDragState();
            return;
        }
        await handleMoveEntry(
            movedEntry,
            draggingCard.sourceResourceKey,
            resourceNode.key,
            row.minutes,
        );
    }, [
        draggingCard,
        effectiveScheduledEntries,
        handleMoveEntry,
        resetDragState,
        scheduledFieldDef?.type,
        scheduledFieldStorageKey,
    ]);

    /**
     * handleSlotClick
     *
     * Creates a scheduled card in an empty slot, or repositions the armed card.
     *
     * @param {object} resourceNode - Resource node for the clicked scheduler column.
     * @param {number} startMinutes - Slot start offset in minutes from the selected day.
     * @returns {Promise<void>} Resolves when the slot action completes.
     */
    const handleSlotClick = useCallback(async (resourceNode, startMinutes) => {
        if (armedMoveCard?.entryKey) {
            const movedEntry = effectiveScheduledEntries.find((entry) => entry.key === armedMoveCard.entryKey) || null;
            if (!movedEntry?.node?.key) {
                resetMoveState();
                return;
            }

            await handleMoveEntry(
                movedEntry,
                armedMoveCard.sourceResourceKey,
                resourceNode?.key,
                startMinutes,
            );
            return;
        }

        if (!canQuickAdd) return;
        await handleSlotCreate(resourceNode, startMinutes);
    }, [
        armedMoveCard,
        canQuickAdd,
        effectiveScheduledEntries,
        handleMoveEntry,
        handleSlotCreate,
        resetMoveState,
    ]);

    /**
     * handleDetachSharedCard
     *
     * Removes the current resource-column edge for a shared scheduled node.
     *
     * @param {object} entry - Scheduled entry being detached from the current column.
     * @param {object} resourceNode - Resource node represented by the current column.
     * @returns {Promise<void>} Resolves when the detach flow completes.
     */
    const handleDetachSharedCard = useCallback(async (entry, resourceNode) => {
        const entryKey = String(entry?.key || '').trim();
        const nodeKey = String(entry?.node?.key || '').trim();
        const resourceKey = String(resourceNode?.key || '').trim();
        const nextResourceKeys = entry?.resourceKeys?.filter(
            (candidateKey) => String(candidateKey || '').trim() !== resourceKey
        ) || [];

        if (!entryKey || !nodeKey || !resourceKey || nextResourceKeys.length === entry?.resourceKeys?.length || nextResourceKeys.length === 0) {
            return;
        }

        const previousOverride = optimisticEntryOverrides?.[entryKey] || null;
        const detachKey = `${resourceKey}:${entryKey}`;

        setDetachingCardKey(detachKey);
        setOptimisticEntryOverrides((prev) => ({
            ...(prev || {}),
            [entryKey]: {
                resourceKeys: nextResourceKeys,
                range: entry.range,
            },
        }));

        try {
            await deleteEdge(resourceKey, nodeKey, {
                baseKey: view?.baseKey || null,
            });
        } catch (error) {
            setOptimisticEntryOverrides((prev) => {
                const next = { ...(prev || {}) };
                if (previousOverride) {
                    next[entryKey] = previousOverride;
                } else {
                    delete next[entryKey];
                }
                return next;
            });
            toast.error(`Could not remove shared assignment: ${error?.message || 'Unknown error'}`);
        } finally {
            setDetachingCardKey((current) => (current === detachKey ? '' : current));
        }
    }, [optimisticEntryOverrides, view?.baseKey]);

    // ----- RENDER ----- //

    return (
        <div className="scheduler-view-container">

            {/* HEADER */}
            <div
              className="scheduler-view-header-card"
            >

                {/* HEADER MAIN */}
                <div className="scheduler-view-header-main">

                    {/* HEADER COPY */}
                    <div className="scheduler-view-header-copy">

                        {/* TITLE */}
                        <div className="scheduler-view-title">{format(selectedDay, 'EEEE, MMMM d, yyyy')}</div>
                    </div>

                    {/* DAY CONTROLS */}
                    <div className="scheduler-view-day-controls">

                        {/* DAY BUTTON */}
                        <button
                          className="scheduler-view-day-button"
                          type="button"
                          onClick={() => setSelectedDay((prev) => addDays(prev, -1))}
                        >
                            Previous
                        </button>

                        {/* DAY BUTTON */}
                        <button
                          className="scheduler-view-day-button"
                          style={{
                            backgroundColor: isSameDay(selectedDay, startOfDay(new Date())) ? theme.backgroundHover : theme.background,
                          }}
                          type="button"
                          onClick={() => setSelectedDay(startOfDay(new Date()))}
                        >
                            Today
                        </button>

                        {/* DAY BUTTON */}
                        <button
                          className="scheduler-view-day-button"
                          type="button"
                          onClick={() => setSelectedDay((prev) => addDays(prev, 1))}
                        >
                            Next
                        </button>
                    </div>
                </div>

                {armedMoveCard?.entryKey && (
                    <div
                      className="scheduler-view-move-banner"
                    >

                        {/* MOVE BANNER COPY */}
                        <div className="scheduler-view-move-banner-copy">
                            Move mode: click a slot to reposition {armedMoveCard.title}.
                        </div>

                        {/* DAY BUTTON */}
                        <button
                          className="scheduler-view-day-button"
                          type="button"
                          onClick={resetMoveState}
                        >
                            Cancel move
                        </button>
                    </div>
                )}

                {touchDragState?.hasMoved && (
                    <div
                      className="scheduler-view-move-banner"
                    >

                        {/* MOVE BANNER COPY */}
                        <div className="scheduler-view-move-banner-copy">
                            Dragging {touchDragState.title}. Release on a slot to move it.
                        </div>

                        {/* DAY BUTTON */}
                        <button
                          className="scheduler-view-day-button"
                          type="button"
                          onClick={resetTouchDragState}
                        >
                            Cancel drag
                        </button>
                    </div>
                )}
            </div>

            {/* STATUS */}
            {!isConfigured && (

                <div
                  className="scheduler-view-empty"
                >
                    Finish configuring the scheduled type and date-time range field to render the scheduler grid.
                </div>
            )}

            {/* STATUS */}
            {isConfigured && error && (

                <div
                  className="scheduler-view-empty"
                >
                    {error}
                </div>
            )}

            {isConfigured && !error && (

                <div className="scheduler-view-grid-shell">

                    {/* GRID HEADER ROW */}
                    <div className="scheduler-view-grid-header-row">

                        {/* TIME HEADER */}
                        <div
                          className="scheduler-view-time-header"
                        ></div>

                        {/* COLUMN HEADERS */}
                        <div className="scheduler-view-column-headers">
                            {safeNodes.map((resourceNode) => (
                                <SchedulerViewColumnHeader
                                  key={resourceNode.key}
                                  resourceNode={resourceNode}
                                />
                            ))}
                        </div>
                    </div>

                    {/* GRID BODY */}
                    <div className="scheduler-view-grid-body">

                        {/* TIME COLUMN */}
                        <div
                          className="scheduler-view-time-column"
                          style={{
                            height: gridHeight,
                          }}
                        >
                            {timeRows.map((row) => (
                                <div
                                  className={`scheduler-view-time-row${row.isHour ? ' scheduler-view-time-row-hour' : ''}`}
                                  style={{
                                    height: rowHeight,
                                  }}
                                  key={row.key}
                                >
                                    {row.label}
                                </div>
                            ))}
                        </div>

                        {/* COLUMNS */}
                        <div className="scheduler-view-columns">
                            {safeNodes.map((resourceNode) => {
                                const columnEntries = columnEntriesByResourceKey.get(String(resourceNode.key)) || [];

                                return (
                                    <div
                                      className="scheduler-view-column"
                                      style={{
                                        height: gridHeight,
                                      }}
                                      key={resourceNode.key}
                                    >
                                        {timeRows.map((row) => (
                                            <SchedulerViewSlot
                                              key={`${resourceNode.key}_${row.key}`}
                                              height={rowHeight}
                                              isHighlighted={Boolean(
                                                    (dragOverSlot?.resourceKey === resourceNode.key && dragOverSlot?.rowKey === row.key)
                                                    || armedMoveCard?.entryKey
                                                )}
                                              isHour={row.isHour}
                                              isInteractive={Boolean(armedMoveCard?.entryKey || canQuickAdd)}
                                              resourceKey={resourceNode.key}
                                              row={row}
                                              onClick={() => {
                                                    handleSlotClick(resourceNode, row.minutes);
                                                }}
                                              onDragOver={(event) => handleSlotDragOver(resourceNode, row, event)}
                                              onDrop={(event) => handleSlotDrop(resourceNode, row, event)}
                                            />
                                        ))}

                                        {columnEntries.map((entry) => {
                                            const timeLabel = `${format(entry.range.start, 'h:mm a')} - ${format(entry.range.end, 'h:mm a')}`;
                                            const laneCount = Math.max(1, entry.layout.laneCount || 1);
                                            const laneIndex = Math.max(0, entry.layout.laneIndex || 0);
                                            const laneWidthPercent = 100 / laneCount;
                                            const detachActionKey = `${resourceNode.key}:${entry.key}`;
                                            const isSharedEntry = entry.resourceKeys.length > 1;
                                            const isDetaching = detachingCardKey === detachActionKey;
                                            const isArmedMove = armedMoveCard?.entryKey === entry.key && armedMoveCard?.sourceResourceKey === resourceNode.key;
                                            const entryCanDrag = canDragCards;
                                            const entryCanResize = canResizeCards;
                                            const resourceColor = typeof resourceNode?.color === 'string'
                                                ? resourceNode.color.trim()
                                                : '';
                                            const cardBackgroundColor = isArmedMove
                                                ? theme.backgroundHover
                                                : getScheduledCardPaletteColor(entry.key, scheduledCardColorOptions, theme.highlight);
                                            const cardForegroundColor = (getHexColorLuminance(cardBackgroundColor) ?? 0) > 0.58
                                                ? '#1f1f1f'
                                                : '#FFFFFF';

                                            return (
                                                <div
                                                  className={`scheduler-view-card${isArmedMove ? ' is-armed-move' : ''}${resizingCard?.entryKey === entry.key ? ' is-resizing' : ''}`}
                                                  style={{
                                                    backgroundColor: cardBackgroundColor,
                                                    color: cardForegroundColor,
                                                    cursor: entryCanDrag ? 'grab' : 'pointer',
                                                    height: entry.layout.height - 2,
                                                    left: `calc(${laneIndex * laneWidthPercent}% + 8px)`,
                                                    opacity: 1,
                                                    paddingBottom: entryCanResize ? 18 : 8,
                                                    right: 'auto',
                                                    top: entry.layout.top,
                                                    width: `calc(${laneWidthPercent}% - 12px)`,
                                                  }}
                                                  key={`${resourceNode.key}_${entry.key}`}
                                                  draggable={entryCanDrag}
                                                  role="button"
                                                  tabIndex={0}
                                                  onClick={(event) => handleCardClick(event, entry)}
                                                  onDragEnd={handleCardDragEnd}
                                                  onDragStart={(event) => handleCardDragStart(entry, resourceNode, event)}
                                                  onKeyDown={(event) => handleCardKeyDown(event, entry)}
                                                  onTouchStart={(event) => handleCardTouchStart(event, entry, resourceNode)}
                                                >
                                                    {/* CARD HEADER */}
                                                    <div className="scheduler-view-card-header">

                                                        {/* CARD TITLE ROW */}
                                                        <div className="scheduler-view-card-title-row">

                                                            {/* RESOURCE DOT CARD */}
                                                            {resourceColor && (
                                                                <span
                                                                  className="scheduler-view-resource-dot scheduler-view-resource-dot-card"
                                                                  style={{
                                                                        backgroundColor: resourceColor,
                                                                    }}
                                                                />
                                                            )}

                                                            {/* CARD TITLE */}
                                                            <div className="scheduler-view-card-title">{entry.node?.title || 'Untitled'}</div>
                                                        </div>

                                                        {/* CARD ACTIONS */}
                                                        <div className="scheduler-view-card-actions">

                                                            {/* CARD ACTION */}
                                                            {isSharedEntry && !isDetaching && (

                                                                <div className="scheduler-view-card-action">
                                                                    <IconButton
                                                                      iconKey="do_not_disturb_on"
                                                                      iconSize={16}
                                                                      title="Remove this shared assignment from the current resource"
                                                                      onClick={(event) => {
                                                                            event?.preventDefault?.();
                                                                            event?.stopPropagation?.();
                                                                            handleDetachSharedCard(entry, resourceNode);
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* CARD TIME */}
                                                    <div
                                                      className="scheduler-view-card-time"
                                                      style={{
                                                        color: cardForegroundColor,
                                                        opacity: 0.78,
                                                      }}
                                                    >
                                                        {timeLabel}
                                                    </div>

                                                    {scheduledPropertyDefs.length > 0 && (

                                                        <div className="scheduler-view-card-properties">
                                                            {scheduledPropertyDefs.map((fieldDef) => {
                                                                const fieldKey = fieldDef?.name || fieldDef?.key || '';
                                                                const rawValue = resolveScheduledFieldValue(entry.node, fieldDef);
                                                                const renderedValue = formatFieldDisplayValue(rawValue, fieldDef?.type);
                                                                if (!fieldKey || !renderedValue) return null;

                                                                return (

                                                                    <div
                                                                      className="scheduler-view-card-property"
                                                                      key={`${entry.key}_${fieldKey}`}
                                                                    >

                                                                        {/* CARD PROPERTY LABEL */}
                                                                        <span
                                                                          className="scheduler-view-card-property-label"
                                                                          style={{
                                                                            color: cardForegroundColor,
                                                                            opacity: 0.78,
                                                                          }}
                                                                        >
                                                                            {fieldDef?.label || fieldKey}
                                                                        </span>

                                                                        {/* CARD PROPERTY VALUE */}
                                                                        <span className="scheduler-view-card-property-value">{renderedValue}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {entryCanResize && (
                                                        <div
                                                          className="scheduler-view-card-resize-handle"
                                                          role="presentation"
                                                          onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                            }}
                                                          onMouseDown={(event) => handleResizeStart(event, entry)}
                                                        >

                                                            {/* CARD RESIZE HANDLE INDICATOR */}
                                                            <div
                                                              className="scheduler-view-card-resize-handle-indicator"
                                                              style={{
                                                                    backgroundColor: resizingCard?.entryKey === entry.key ? theme.foreground : theme.foregroundMuted,
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* COLUMN EMPTY */}
                                        {!isLoading && columnEntries.length === 0 && (

                                            <div className="scheduler-view-column-empty"></div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* LOADING */}
                    {isLoading && (

                        <div
                          className="scheduler-view-loading"
                        >
                            Loading scheduled records...
                        </div>
                    )}
                </div>
            )}

            {/* TOUCH GHOST */}
            {touchDragState?.hasMoved && (
                <div
                  className="scheduler-view-touch-ghost"
                  style={{
                    left: touchDragState.currentClientX + 12,
                    top: touchDragState.currentClientY + 12,
                  }}
                >
                    {touchDragState.title}
                </div>
            )}
        </div>
    );
};

// Dev Tools
SchedulerView.displayName = 'SchedulerView';

export default SchedulerView;
